import React, { useEffect, useState, useRef } from "react";
import { Box, Flex, IconButton, Image, Text, Tooltip, } from "@chakra-ui/react";
import { pxToRem } from "../../../utils/editor.ts";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { RephraseResult } from "../../../types/Writer/RephraseSentencesResult.ts";
import useOutlineStore from "../../../stores/modules/outline.ts";
import { OutlineNodeType } from "../../../types/Writer/OutlineNode.ts";
import { cloneDeep } from "lodash";
import { MathJax } from "better-react-mathjax";
import { TableRender } from "../InserTool/TableRender.tsx";
import parse from "html-react-parser";
import useAiTypeStore from "../../../stores/modules/aiType.ts";

interface RephraseProps {
  result: RephraseResult
}

export const Rephrase: React.FC<RephraseProps> = (
  {
    result
  }
) => {
  const [renderContent, setRenderContent] = useState(null)
  const { outline,setOutline,nodeList, setOutlineIsChange } = useOutlineStore()
  const { highlightState, setHighlightState, clearHighlight } = useAiTypeStore()
  const cardRef = useRef<HTMLDivElement>(null)
  const isHighlighted = highlightState.nodeId === result.id && highlightState.selectContent === result.select_content

  // 点击卡片时高亮对应文本
  const handleCardClick = () => {
    if (isHighlighted) {
      // 如果已经高亮，则取消
      clearHighlight()
    } else {
      // 获取卡片位置
      const cardRect = cardRef.current?.getBoundingClientRect() || null
      setHighlightState({
        nodeId: result.id,
        selectContent: result.select_content,
        cardRect,
      })
    }
  }

  useEffect(() => {
    const content = result?.select_content_rephrased_result

    if (content?.indexOf('($') > -1) {
      // Recursively find node by id (handles nodes nested inside ol/ul)
      const findNodeById = (nodes: any[], id: number): any => {
        for (const n of nodes) {
          if (n.id === id) return n;
          if (n.children?.length) {
            const found = findNodeById(n.children, id);
            if (found) return found;
          }
        }
        return undefined;
      };
      const node = findNodeById(nodeList, result.id)
      const replacedContent = result.select_content_rephrased_result.split(/(\(\$\w+\{\d+}\$\))/).map((part, index) => {
        const match = part.match(/\$(\w+){(\d+)}\$/);
        // let length = part.length
        if (match) {
          const id = Number(match[2]);
          const slotItem = node?.[match[1]]?.[id];
          if (slotItem) {

            const content = '($' + slotItem.value + '$)'
            return (
              <React.Fragment key={`${index}-${id}`}>
                {match[1] === 'equation' && <Box
                    as="span"
                    mx={'5px'}
                    _hover={{ bg: "#e8e7e8", cursor: "pointer" }}
                    data-content={part}
                    data-id={id}
                    data-type={match[1]}>
                    <MathJax inline>{content}</MathJax>
                </Box>}
                {match[1] === 'footnote' && <Box
                    as="span"
                    mx={'5px'}
                    _hover={{ bg: "#e8e7e8", cursor: "pointer" }}
                    data-content={part}
                    data-type={match[1]}
                    data-id={id}
                    fontSize={pxToRem(14)}
                    color={'#224ea8'}
                    fontStyle={'italic'}>
                  {slotItem.value}
                </Box>}
                {match[1] === 'cite' && <Box
                    color={'blue.500'}
                    as="span"
                    mx={'5px'}
                    _hover={{ bg: "#e8e7e8", cursor: "pointer" }}
                    data-content={part}
                    data-id={id}
                    data-type={match[1]}>
                    <Tooltip hasArrow padding={4} bg={'#FFF'} color={'#333'} label={slotItem.value}
                             aria-label='Description'>
                      {`[${slotItem.id}]`}
                    </Tooltip>
                </Box>}
                {match[1] === 'refer' &&
                    <Box color={'blue.500'}
                         as="span"
                         mx={'5px'}
                         _hover={{ bg: "#e8e7e8", cursor: "pointer" }}
                         data-inline={true}
                         data-content={part}
                         data-id={id}
                         data-type={match[1]}>
                      {slotItem.type === OutlineNodeType.Figure &&
                          <Tooltip hasArrow padding={0} label={slotItem.content?.indexOf('base64') > -1 ?
                            <Image width="200px" alt="Reference Preview" src={slotItem.content}/>
                            : <Image width="200px" alt="Reference Preview"
                                     src={'data:image/png;base64,' + slotItem.content}/>
                          } aria-label='Description'>
                            {`[${slotItem.value}]`}
                          </Tooltip>}
                      {slotItem.type === OutlineNodeType.Equation &&
                          <Tooltip hasArrow padding={4} bg={'#FFF'} color={'#333'} label={
                            <MathJax inline>(${slotItem.content}$)</MathJax>
                          } aria-label='Description'>
                            {`[${slotItem.value}]`}
                          </Tooltip>}
                      {slotItem.type === OutlineNodeType.Table &&
                          <Tooltip hasArrow padding={4} bg={'#FFF'} color={'#333'} label={
                            <TableRender content={slotItem.content}/>
                          } aria-label='Description'>
                            {`[${slotItem.value}]`}
                          </Tooltip>}
                      {slotItem.type === OutlineNodeType.Section && `[${slotItem.value}]`}
                    </Box>}
                &#xFEFF;
              </React.Fragment>
            )
          }
        }
        if (part.indexOf('<strong') > -1 || part.indexOf('<em') > -1 || part.indexOf('<u') > -1) {
          return <React.Fragment key={index}>{parse(part)}</React.Fragment>;
        }
        return <React.Fragment key={`${content}-${index}`}>{part?.replace(/\uFEFF/g, '')}</React.Fragment>;
      })
      setRenderContent(replacedContent)
    }
  }, [])
  return (
    <Box
      ref={cardRef}
      onClick={handleCardClick}
      cursor="pointer"
      p={2}
      borderRadius="md"
      border={isHighlighted ? "2px solid #3898FF" : "2px solid transparent"}
      bg={isHighlighted ? "#EBF5FF" : "transparent"}
      transition="all 0.2s"
      _hover={{ bg: isHighlighted ? "#EBF5FF" : "#F5F5F5" }}
      position="relative"
      data-highlight-card={isHighlighted ? "true" : undefined}
    >
      {/* 高亮时显示原文 */}
      {isHighlighted && (
        <Box mb={2} p={2} bg="#FFF9E6" borderRadius="md" fontSize={pxToRem(12)}>
          <Text color="#666" fontWeight="bold" mb={1}>Original:</Text>
          <Text color="#333">{result.select_content}</Text>
        </Box>
      )}
      <Text
        width={pxToRem(248)}
        aria-multiline={true}
        fontSize={pxToRem(14)}
        fontWeight={400}
      >
        {renderContent ? renderContent : parse(result.select_content_rephrased_result || "")}
      </Text>
      <Flex width={pxToRem(248)}
            alignItems={"center"}
            justifyContent={"start"}>
        <IconButton
          mr={"10px"}
          size={"sm"}
          minWidth={pxToRem(16)}
          width={pxToRem(16)}
          bg="transparent"
          p={0}
          _hover={{ bg: 'transparent' }}
          icon={
            <Image
              w={pxToRem(16)}
              objectFit="cover"
              src={"/static/ToolAI/useContent.png"}
            />
          }
          aria-label="replace"
          onClick={(e) => {
            e.stopPropagation()
            // 清除高亮
            clearHighlight()
            if (!result.replaced) {
              const newNodeList = cloneDeep(outline);

              // 递归查找并替换内容
              const replaceContentInNode = (nodes) => {
                for (const node of nodes) {
                  if (node.id === result.id) {
                    // 找到目标节点，执行替换
                    switch (node.type) {
                      case OutlineNodeType.Section:
                        node.name = node.name.replace(result.select_content, result.select_content_rephrased_result);
                        return true;
                      case OutlineNodeType.Paragraph:
                        node.content = node.content.replace(result.select_content, result.select_content_rephrased_result);
                        return true;
                    }
                  }

                  // 递归查找子节点
                  if (node.children && node.children.length) {
                    const replaced = replaceContentInNode(node.children);
                    if (replaced) return true;
                  }
                }
                return false;
              };

              replaceContentInNode(newNodeList.nodes);
              result.replaced = true;
              setOutlineIsChange(true);

              // 更新 outline
              setOutline(newNodeList);
              window.getSelection()?.removeAllRanges();
            }
          }}
        />
        {result.replaced && <CheckCircleIcon
            sx={{
              color: 'hsla(128, 71%, 50%, 0.8)'
            }}/>}
      </Flex>
    </Box>
  )
}